import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Popover, Spin } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import type { InputNode, ReportListItem, ReportListResult } from '../types';
import { parseReportUrl } from '../utils/parseReportUrl';
import { usePopupHost } from '../PopupHostContext';
import { InfoIconTooltip } from './InfoIconTooltip';

type ReportListFetcher = (params: {
  nodeKey: string;
  nodeType: 'report-url' | 'multi-variable' | 'multi-variable-select';
}) => Promise<ReportListResult>;

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  onParse?: (url: string) => Promise<void>;
  /** 当传入时，chip 右侧显示下拉按钮 → 弹出报告列表选择面板 */
  reportListFetch?: ReportListFetcher;
  /** 空态"去生成"跳转 */
  onReportGoGenerate?: (agentType: string | number) => void;
  /** 解析后的报告名：传入时 chip 切到 name 模式 */
  reportName?: string;
  /** name chip 点击清除按钮 */
  onClearReport?: () => void;
  /** dropdown item 的"查看报告"按钮：不传则 window.open(viewUrl||url) */
  onViewReport?: (item: ReportListItem) => void;
  /** dropdown 隐藏当前会话报告 tab */
  hideCurrentSessionReport?: boolean;
  disabled?: boolean;
  error?: string;
};

const EMPTY_IMG_URL =
  'https://sealseek-public-read.oss-cn-hangzhou.aliyuncs.com/file/website/kongzhuangtai.svg';

const normalizeTooltip = (t: InputNode['tooltip']): string => {
  if (!t) return '';
  return typeof t === 'string' ? t : JSON.stringify(t);
};

const INPUT_STYLE: React.CSSProperties = {
  display: 'inline-block',
  border: 0,
  background: 'transparent',
  outline: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
};

/** 估算文字像素宽度（中文 16px / 西文 9px），给 input 显式 width 让 chip 按内容撑开。 */
const estimateTextPx = (text: string): number => {
  let w = 0;
  for (const c of text) w += /[一-龥＀-￯]/.test(c) ? 16 : 9;
  return w;
};

/** 用于 URL 输入态时的精确匹配：reportId 相同则视为同一个报告。 */
const reportMatchKey = (url: string): string => {
  if (!url) return '';
  const parsed = parseReportUrl(url);
  return parsed.reportId ? `report:${parsed.reportId}` : `url:${url.trim()}`;
};

/** Popover content：tabs + 列表 + 空态 + 查看报告按钮 + is-scrolling 动画。 */
const DropdownPanel: React.FC<{
  nodeKey: string;
  fetch: ReportListFetcher;
  onSelect: (item: ReportListItem) => void;
  onGoGenerate?: (agentType: string | number) => void;
  onViewReport?: (item: ReportListItem) => void;
  hideCurrentSessionReport?: boolean;
  preciseMatchKey: string;
  selectedReportKey: string;
  open: boolean;
}> = ({
  nodeKey,
  fetch,
  onSelect,
  onGoGenerate,
  onViewReport,
  hideCurrentSessionReport,
  preciseMatchKey,
  selectedReportKey,
  open,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ReportListResult | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string>('');
  const seqRef = useRef(0);
  const scrollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const seq = ++seqRef.current;
    setLoading(true);
    fetch({ nodeKey, nodeType: 'report-url' })
      .then((r) => {
        if (seq !== seqRef.current) return;
        const tabs = hideCurrentSessionReport
          ? r.tabs.filter((t) => t.key !== 'currentConversationReports')
          : r.tabs;
        const filtered: ReportListResult = { ...r, tabs };
        setData(filtered);
        const firstNonEmpty = tabs.find((t) => t.items.length > 0);
        setActiveTabKey(firstNonEmpty?.key ?? tabs[0]?.key ?? '');
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setData({ tabs: [] });
      })
      .finally(() => {
        if (seq !== seqRef.current) return;
        setLoading(false);
      });
  }, [open, fetch, nodeKey, hideCurrentSessionReport]);

  // precise 模式：定位到第一个含匹配 item 的 tab
  const filterItems = useCallback(
    (items: ReportListItem[]) =>
      preciseMatchKey
        ? items.filter((i) => reportMatchKey(i.url) === preciseMatchKey)
        : items,
    [preciseMatchKey],
  );

  useEffect(() => {
    if (!open || !preciseMatchKey || !data) return;
    const matched = data.tabs.find((t) => filterItems(t.items).length > 0);
    if (matched) setActiveTabKey((p) => (p === matched.key ? p : matched.key));
  }, [open, preciseMatchKey, data, filterItems]);

  const activeTab = data?.tabs.find((t) => t.key === activeTabKey);
  const items = activeTab ? filterItems(activeTab.items) : [];
  const hasTabs = (data?.tabs.length ?? 0) > 1;

  const handleListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    el.classList.add('is-scrolling');
    if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = window.setTimeout(() => {
      el.classList.remove('is-scrolling');
      scrollTimerRef.current = null;
    }, 300);
  }, []);

  const handleViewReport = useCallback(
    (e: React.MouseEvent, item: ReportListItem) => {
      e.stopPropagation();
      if (onViewReport) {
        onViewReport(item);
      } else {
        window.open(item.viewUrl || item.url, '_blank');
      }
    },
    [onViewReport],
  );

  return (
    <div
      className={`rte-report-dropdown-panel${
        hasTabs ? ' rte-report-dropdown-panel-with-tabs' : ''
      }`}
      onMouseDown={(e) => e.preventDefault()}
    >
      {loading ? (
        <div className="rte-report-dropdown-loading">
          <Spin size="small" />
        </div>
      ) : data ? (
        <>
          {hasTabs && (
            <div className="rte-report-dropdown-tab-outer">
              <div className="rte-report-dropdown-tab-inner">
                {data.tabs.map((t) => (
                  <div
                    key={t.key}
                    className={`rte-report-dropdown-tab-item${
                      t.key === activeTabKey ? ' active' : ''
                    }`}
                    onClick={() => setActiveTabKey(t.key)}
                  >
                    {t.label}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rte-report-dropdown-list" onScroll={handleListScroll}>
            {!activeTab || items.length === 0 ? (
              <div className="rte-report-dropdown-empty">
                <img
                  className="rte-report-dropdown-empty-img"
                  src={EMPTY_IMG_URL}
                  alt=""
                />
                <div className="rte-report-dropdown-empty-text">
                  <span className="rte-report-dropdown-empty-label">
                    {preciseMatchKey ? '未找到匹配的报告' : '暂无生成的报告'}
                  </span>
                  {onGoGenerate && activeTab?.agentType !== undefined && (
                    <span
                      className="rte-report-dropdown-empty-link"
                      onClick={() => onGoGenerate(activeTab.agentType!)}
                    >
                      去生成
                    </span>
                  )}
                </div>
              </div>
            ) : (
              items.map((item, idx) => (
                <div
                  key={`${item.url}-${idx}`}
                  className={`rte-report-dropdown-item${
                    reportMatchKey(item.url) === selectedReportKey
                      ? ' rte-report-dropdown-item-selected'
                      : ''
                  }`}
                  onClick={() => onSelect(item)}
                >
                  <span className="rte-report-dropdown-item-name">
                    {item.name}
                  </span>
                  <span
                    className="rte-report-dropdown-item-action"
                    onClick={(e) => handleViewReport(e, item)}
                  >
                    <i className="iconfont icon-chakanbaogao" />
                    查看报告
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export const ReportUrlNode: React.FC<Props> = React.memo(
  ({
    node,
    value,
    onChange,
    onParse,
    reportListFetch,
    onReportGoGenerate,
    reportName,
    onClearReport,
    onViewReport,
    hideCurrentSessionReport,
    disabled,
    error,
  }) => {
    const popupHost = usePopupHost();
    const tooltip = normalizeTooltip(node.tooltip);
    const text = value ?? '';
    const hasName = !!reportName;
    const isEmpty = !text && !hasName;
    const hasFetch = !!reportListFetch;
    // name chip 也保留 dropdown icon（quiet 模式，hover 时让位给 clear icon）
    const showDropdown = hasFetch && !disabled && (isEmpty || hasName);
    const showInfoIcon = !hasFetch && Boolean(tooltip) && isEmpty;
    const showWithIconClass = showDropdown || showInfoIcon;

    const [parsing, setParsing] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const broadcastId = useRef({}).current;

    // 实际触发解析：去重（同串不重复请求）+ loading 态。onBlur / onPaste / 选下拉共用。
    // 不做 https 正则前置过滤——抖音分享串/口令等非标准 URL 由上层 onParse 判断有效性，
    // 仅「空内容不触发」（对齐 V1 report-url/hooks.ts:31-32 失焦解析语义）。
    // 解析失败的清空由上层 onReportUrlParse 负责：value 被清空 → text 变空 →
    // 下面的 useEffect 重置 lastParsedRef，用户可重新粘贴同一 URL 重试。
    const lastParsedRef = useRef<string>('');
    const runParse = useCallback(
      async (raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed || !onParse) return;
        if (lastParsedRef.current === trimmed) return; // 去重：同一串不重复解析
        lastParsedRef.current = trimmed;
        setParsing(true);
        try {
          await onParse(trimmed);
        } finally {
          setParsing(false);
        }
      },
      [onParse],
    );

    // value 被清空（含解析失败后上层清空）时重置去重标记，允许重新粘贴同一 URL 重试
    useEffect(() => {
      if (!text) lastParsedRef.current = '';
    }, [text]);

    const handleBlur = useCallback(() => {
      void runParse(text);
    }, [runParse, text]);

    // V2 粘贴即解析：受控 input 能直接从 clipboardData 拿到干净文本，无 V1 contentEditable
    // 「innerText 夹带 cursor 残留 / 上次 chip 状态」问题（V1 故意 blur-only，见 pasteHandler.ts），
    // 故可安全在粘贴瞬间解析。preventDefault 避免默认粘贴与手动 onChange 双写。
    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (!node.key) return;
        const pasted = e.clipboardData.getData('text/plain');
        if (!pasted.trim()) return;
        e.preventDefault();
        onChange(node.key, pasted);
        setDropdownOpen(false);
        void runParse(pasted);
      },
      [node.key, onChange, runParse],
    );

    const openDropdown = useCallback(() => {
      // 广播：通知其它 dropdown 关闭，保证全局只有一个 open
      document.dispatchEvent(
        new CustomEvent('rte-report-dropdown-opening', { detail: broadcastId }),
      );
      setDropdownOpen(true);
    }, [broadcastId]);

    const handleSelectReport = useCallback(
      (item: ReportListItem) => {
        if (!node.key) return;
        onChange(node.key, item.url);
        setDropdownOpen(false);
        // 选已有报告也走 runParse（去重 + loading 一致）。仅 http(s) 才解析，
        // 失败的清空同样由上层 onReportUrlParse 负责（与粘贴/失焦同路径）。
        if (/^https?:\/\//i.test(item.url)) void runParse(item.url);
      },
      [node.key, onChange, runParse],
    );

    const handleClearName = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (node.key) onChange(node.key, '');
        onClearReport?.();
      },
      [node.key, onChange, onClearReport],
    );

    // 监听其它 dropdown 的开启广播：detail 不是自己则关闭
    useEffect(() => {
      const handler = (e: Event) => {
        const detail = (e as CustomEvent).detail;
        if (detail !== broadcastId) setDropdownOpen(false);
      };
      document.addEventListener('rte-report-dropdown-opening', handler);
      return () =>
        document.removeEventListener('rte-report-dropdown-opening', handler);
    }, [broadcastId]);

    const chipClasses = [
      'variable-chip',
      'rte-report-url',
      hasName ? 'rte-report-name-chip' : '',
      error ? 'rte-report-url-error' : '',
      showWithIconClass ? 'rte-variable-with-icon' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const hostRef = useRef<HTMLSpanElement>(null);
    const chipRef = useRef<HTMLSpanElement>(null);

    const [placement, setPlacement] = useState<'bottomLeft' | 'topLeft'>('bottomLeft');

    const updatePlacement = useCallback(() => {
      const chip = chipRef.current;
      if (!chip) return;
      const rect = chip.getBoundingClientRect();
      const PANEL_H = 236;
      const spaceBelow = window.innerHeight - rect.bottom;
      setPlacement(spaceBelow < PANEL_H ? 'topLeft' : 'bottomLeft');
    }, []);

    useLayoutEffect(() => {
      if (dropdownOpen) updatePlacement();
    }, [dropdownOpen, updatePlacement]);

    const popoverAlign = useMemo(
      () => ({
        offset: [0, placement === 'bottomLeft' ? 4 : -4],
        overflow: { adjustX: false, adjustY: false },
      }),
      [placement],
    );

    useEffect(() => {
      if (!dropdownOpen) return;
      const onDocClick = (e: MouseEvent) => {
        const chip = chipRef.current;
        const t = e.target as Node;
        if (chip?.contains(t)) return;
        const overlays = document.querySelectorAll('.rte-report-dropdown-overlay');
        for (let i = 0; i < overlays.length; i++) {
          if (overlays[i].contains(t)) return;
        }
        setDropdownOpen(false);
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [dropdownOpen]);

    // dropdown host：name chip 模式下加 quiet 类（与 clear icon 共位 hover 互斥）
    const dropdownHostEl = showDropdown ? (
      <span
        ref={hostRef}
        className={[
          'rte-report-dropdown-host',
          hasName ? 'rte-report-dropdown-host-quiet' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-key={node.key}
        data-node-type="report-url"
        contentEditable={false}
        onClick={(e) => {
          e.stopPropagation();
          if (dropdownOpen) {
            setDropdownOpen(false);
          } else {
            openDropdown();
          }
        }}
      >
        <i
          className="iconfont icon-qiehuan-xiayizhang rte-report-dropdown-icon"
          aria-label="选择报告"
        />
      </span>
    ) : showInfoIcon ? (
      <InfoIconTooltip
        tooltip={tooltip}
        className="variable-info-icon"
        dataVariableKey={node.key}
      />
    ) : null;

    // name chip 模式下的 clear host（hover chip 时显示）
    const clearHostEl = hasName ? (
      <span
        className="rte-report-chip-clear-host"
        contentEditable={false}
        onMouseDown={handleClearName}
      >
        <span className="rte-report-chip-clear-icon" title="清除">
          <CloseCircleFilled />
        </span>
      </span>
    ) : null;

    const placeholderText = node.placeholder ?? '粘贴报告链接';
    const measureFor = text || placeholderText;
    const mirrorRef = useRef<HTMLSpanElement>(null);
    const [measuredWidth, setMeasuredWidth] = useState(0);
    useLayoutEffect(() => {
      if (mirrorRef.current) setMeasuredWidth(mirrorRef.current.offsetWidth);
    });
    const inputWidth = measuredWidth > 0 ? Math.max(60, measuredWidth + 2) : estimateTextPx(measureFor) + 6;

    const preciseMatchKey = useMemo(() => {
      if (hasName) return '';
      const trimmed = text.trim();
      if (!/^https?:\/\//i.test(trimmed)) return '';
      return reportMatchKey(trimmed);
    }, [text, hasName]);
    const selectedReportKey = useMemo(
      () => (/^https?:\/\//i.test(text.trim()) ? reportMatchKey(text.trim()) : ''),
      [text],
    );

    // V1 parity：name chip 整体可点击打开 dropdown 换报告（除 clear / dropdown 自己 / disabled）
    const handleChipClick = useCallback(
      (e: React.MouseEvent) => {
        if (!hasName || disabled || !showDropdown) return;
        const target = e.target as HTMLElement;
        if (target.closest('.rte-report-chip-clear-host')) return;
        if (target.closest('.rte-report-dropdown-host')) return; // 由 host 自己的 onClick 处理
        e.stopPropagation();
        if (dropdownOpen) setDropdownOpen(false);
        else openDropdown();
      },
      [hasName, disabled, showDropdown, dropdownOpen, openDropdown],
    );

    const chipEl = (
      <span
        ref={chipRef}
        className={chipClasses}
        data-type="report-url"
        data-key={node.key}
        data-placeholder={node.placeholder ?? ''}
        data-group={node.exclusionGroup}
        data-tooltip={tooltip || undefined}
        data-report-url={hasName ? text || true : undefined}
        data-report-name={reportName || undefined}
        onClick={handleChipClick}
      >
        <span
          ref={mirrorRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'pre',
            font: 'inherit',
            pointerEvents: 'none',
            top: 0,
            left: 0,
          }}
        >
          {measureFor}
        </span>
        {hasName ? (
          <span className="rte-report-name-chip-label">{reportName}</span>
        ) : (
          <input
            data-type="report-url"
            data-key={node.key}
            value={text}
            onChange={(e) => onChange(node.key!, e.target.value)}
            onBlur={handleBlur}
            onPaste={handlePaste}
            onClick={() => {
              if (!showDropdown || text.trim()) return;
              const host = chipRef.current?.querySelector('.rte-report-dropdown-host') as HTMLElement | null;
              host?.click();
            }}
            disabled={disabled || parsing}
            placeholder={placeholderText}
            style={{ ...INPUT_STYLE, width: inputWidth }}
          />
        )}
        {dropdownHostEl}
        {clearHostEl}
      </span>
    );

    const dropdownContent = showDropdown && dropdownOpen ? (
      <DropdownPanel
        nodeKey={node.key!}
        fetch={reportListFetch!}
        onSelect={handleSelectReport}
        onGoGenerate={onReportGoGenerate}
        onViewReport={onViewReport}
        hideCurrentSessionReport={hideCurrentSessionReport}
        preciseMatchKey={preciseMatchKey}
        selectedReportKey={selectedReportKey}
        open={dropdownOpen}
      />
    ) : null;

    if (!node.required) {
      return (
        <>
          <Popover
            content={dropdownContent}
            trigger={[]}
            open={showDropdown && dropdownOpen}
            placement={placement}
            align={popoverAlign}
            autoAdjustOverflow={false}
            getPopupContainer={() => popupHost ?? document.body}
            overlayClassName="rte-report-dropdown-overlay"
            arrow={false}
          >
            {chipEl}
          </Popover>
        </>
      );
    }
    return (
      <>
        <span
          className="exclusion-wrapper required-upload-group"
          data-group={node.exclusionGroup}
        >
          <span className="rte-text-required">
            <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
          </span>
          <Popover
            content={dropdownContent}
            trigger={[]}
            open={showDropdown && dropdownOpen}
            placement={placement}
            align={popoverAlign}
            autoAdjustOverflow={false}
            getPopupContainer={() => popupHost ?? document.body}
            overlayClassName="rte-report-dropdown-overlay"
            arrow={false}
          >
            {chipEl}
          </Popover>
        </span>
      </>
    );
  },
);
ReportUrlNode.displayName = 'ReportUrlNode';
